import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { adminPaginationSchema } from './users.screen.js'

/*
 * «Correo › Registro»: one page of the platform e-mail log, the counts of the period and the organization and
 * template filter options in one answer. Each schema mirrors an interface of `contracts/admin/email.types.ts` and the
 * type assertions of `packages/backend/src/platform/server/__tests__/shared-email-logs-screen.type-assertions.ts` tie
 * them in both directions. Dates travel as ISO text; the counts and the facets are the ones of the PERIOD (`days`),
 * without the other filters: the strip always shows the unfiltered totals.
 */

const emailLogStatusSchema = z.enum(['pending', 'queued', 'sent', 'delivered', 'failed', 'bounced'])

export const adminEmailLogRowSchema = z.strictObject({
  id: z.string(),
  tenantId: z.string(),
  tenantName: z.string().nullable(),
  recipientEmail: z.string(),
  subject: z.string(),
  templateCode: z.string().nullable(),
  templateName: z.string().nullable(),
  status: emailLogStatusSchema,
  providerName: z.string().nullable(),
  deliverySource: z.enum(['tenant', 'platform', 'console_fallback', 'none']).nullable(),
  errorMessage: z.string().nullable(),
  suppressionReason: z.string().nullable().optional(),
  suppressionBypassed: z.string().nullable().optional(),
  queuedAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  failedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const adminEmailLogCountsSchema = z.strictObject({
  total: z.number(),
  failed: z.number(),
  noProvider: z.number(),
  unacknowledged: z.number(),
  delivered: z.number(),
  bounced: z.number(),
  inProgress: z.number(),
})

export const adminEmailLogFacetsSchema = z.strictObject({
  organizations: z.array(z.strictObject({ id: z.string(), name: z.string(), count: z.number() })),
  templates: z.array(
    z.strictObject({ code: z.string(), name: z.string().nullable(), count: z.number() }),
  ),
})

/**
 * The names are the ones of `GET /api/admin/email/logs`, so the URL of the page and the journeys do not change.
 * `tenantId` is `guid`: the seeded organizations carry no version nibble (`email-logs.routes.ts`). A `limit` over 100
 * is a 400 (the old route clamped it in silence).
 */
export const adminEmailLogsQuerySchema = z.strictObject({
  days: z.coerce.number<number>().int().min(1).max(3650).default(30),
  page: z.coerce.number<number>().int().min(1).default(1),
  limit: z.coerce.number<number>().int().min(1).max(100).default(25),
  status: emailLogStatusSchema.optional(),
  failure: z.enum(['no_provider']).optional(),
  acknowledged: z.enum(['false']).optional(),
  tenantId: z.guid().optional(),
  templateCode: z.string().min(1).max(100).optional(),
  search: z.string().min(1).max(320).optional(),
})

export const adminEmailLogsScreen = defineScreen({
  id: 'admin.email.logs',
  path: '/api/screens/admin/email/logs',
  access: 'super_admin',
  query: adminEmailLogsQuerySchema,
  response: z.strictObject({
    page: screenSection(
      z.strictObject({
        items: z.array(adminEmailLogRowSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    counts: screenSection(adminEmailLogCountsSchema),
    facets: screenSection(adminEmailLogFacetsSchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.email.logs'],
  budget: { requests: 1, statements: 10, bytes: 150_000, p95Ms: 400 },
  summary:
    'E-mail log: one page of the platform log, the counts of the period and the organization and template filter options',
})
export type AdminEmailLogRow = z.infer<typeof adminEmailLogRowSchema>
/** The counts of the PERIOD as the screen carries them: no `period` (it is the query's). */
export type AdminEmailLogCounts = z.infer<typeof adminEmailLogCountsSchema>
export type AdminEmailLogFacets = z.infer<typeof adminEmailLogFacetsSchema>
export type AdminEmailLogsQuery = ScreenQuery<typeof adminEmailLogsScreen>
export type AdminEmailLogsData = ScreenData<typeof adminEmailLogsScreen>
